// @ts-check
/**
 * Pure state for the new-campaign wizard: the reducer, what each step still needs, and the request
 * bodies built from it. No React and no service calls, so it is cheap to test through the wizard.
 */
import { MAX_CSV_ROWS, parseCsvAudience } from '../../utils/csvAudience';
import { WHATSAPP_MESSAGE_SOURCE as SOURCE, templateVariableCount, variableChoicesFor } from '../../utils/whatsapp';

export const STEPS = ['Audience', 'Recipients', 'Message', 'Send'];
export const LAST_STEP = STEPS.length - 1;

export const SOURCE_OPTIONS = [
  { value: SOURCE.WALK_IN_LEAD, label: 'Event leads' },
  { value: SOURCE.CUSTOMER, label: 'Past customers' },
  { value: SOURCE.ATELIER_CONTACT, label: 'Atelier contacts' },
  { value: SOURCE.CSV, label: 'Imported list' },
];

export const ORDERED_WITHIN_OPTIONS = [
  { value: '', label: 'Any time' },
  { value: '30', label: 'Last 30 days' },
  { value: '90', label: 'Last 90 days' },
  { value: '180', label: 'Last 180 days' },
  { value: '365', label: 'Last 365 days' },
];

/** Values a preview shows for placeholders the preview response has no per-person data for. */
const SAMPLE_VALUES = {
  event_name: 'Your event',
  event_venue: 'Your venue',
  event_date: '26 Sep 2026',
  style: 'Style',
  size_tried: 'M',
  sender_name: 'Team member',
  sender_position: 'Stylist',
  product_name: 'the dress',
};
export const SAMPLED_SOURCES = Object.keys(SAMPLE_VALUES);

export const initialState = {
  step: 0,
  source: /** @type {string} */ (SOURCE.WALK_IN_LEAD),
  eventIds: /** @type {string[]} */ ([]),
  anyCountry: true,
  country: '',
  orderedWithin: '',
  csvText: '',
  csvCountry: '',
  excluded: /** @type {string[]} */ ([]),
  templateId: '',
  variableMap: /** @type {Record<string, string>} */ ({}),
  previewAs: 0,
  name: '',
  testPhone: '',
  testCountry: '',
  senderName: '',
  senderPosition: '',
};
/** @typedef {typeof initialState} WizardState */

let lastParse = { text: /** @type {string | null} */ (null), result: parseCsvAudience('') };
/** The parse of the pasted list, remembered for the last text so every render does not re-parse it. */
export const parsedCsv = (/** @type {string} */ text) => {
  if (lastParse.text !== text) lastParse = { text, result: parseCsvAudience(text) };
  return lastParse.result;
};

/** What is wrong with the audience inputs, or '' when they are ready to preview. */
export function audienceProblem(/** @type {WizardState} */ s) {
  if (s.source === SOURCE.WALK_IN_LEAD && s.eventIds.length === 0) return 'Pick at least one event';
  if (s.source === SOURCE.CUSTOMER && !s.anyCountry && !s.country) return 'Choose a country or tick Any country';
  if (s.source === SOURCE.CSV) {
    const { rows, errors, tooMany } = parsedCsv(s.csvText);
    if (errors.length > 0) return `Fix ${errors.length} ${errors.length === 1 ? 'line' : 'lines'} before continuing`;
    if (tooMany) return `An imported list is limited to ${MAX_CSV_ROWS} people`;
    if (rows.length === 0) return 'Add at least one name and phone number';
    if (!s.csvCountry && rows.some((r) => !r.phone.startsWith('+'))) {
      return 'Choose the country for numbers without a +';
    }
  }
  return '';
}

/**
 * The audience half of every request, or null while the inputs are incomplete.
 * @param {WizardState} s
 * @returns {{source: string, filters: Record<string, any>} | null}
 */
export function buildAudience(s) {
  if (audienceProblem(s)) return null;
  if (s.source === SOURCE.WALK_IN_LEAD) return { source: s.source, filters: { event_ids: [...s.eventIds].sort() } };
  if (s.source === SOURCE.CUSTOMER) {
    /** @type {Record<string, any>} */
    const filters = {};
    if (!s.anyCountry) filters.country = s.country;
    if (s.orderedWithin) filters.ordered_within_days = Number(s.orderedWithin);
    return { source: s.source, filters };
  }
  if (s.source === SOURCE.CSV) {
    const rows = parsedCsv(s.csvText).rows.map((r) => ({ name: r.name, phone: r.phone, country: s.csvCountry }));
    return { source: s.source, filters: { rows } };
  }
  return { source: s.source, filters: {} };
}

export const audienceKey = (/** @type {WizardState} */ s) => JSON.stringify(buildAudience(s));
export const excludedIds = (/** @type {WizardState} */ s) => [...s.excluded].sort();

/** The body of previewAudience for the current inputs, or null while they are incomplete. */
export function previewBody(/** @type {WizardState} */ s) {
  const audience = buildAudience(s);
  return audience && { ...audience, exclude_source_ids: excludedIds(s) };
}

/** Placeholder slots a template needs, as [1..N]; N is its highest {{n}}. */
export const slotsFor = (/** @type {{body?: string} | null | undefined} */ template) =>
  Array.from({ length: templateVariableCount(template?.body) }, (_, i) => String(i + 1));

/** A link button whose address holds a {{n}} needs a value per message, not a single campaign-wide one. */
export const needsPerMessageLink = (/** @type {{buttons?: {url?: string}[]} | null | undefined} */ template) =>
  (template?.buttons || []).some((b) => String(b.url || '').includes('{{'));

/** Whether this template's header is an image, which also needs a value per message. */
export const needsHeaderImage = (/** @type {{header_image?: boolean} | null | undefined} */ template) =>
  Boolean(template?.header_image);

/**
 * The only sources that can fill each reserved key (mirrors backend RESERVED_SOURCES): an image URL
 * for the header, and a suffix for the button's base URL - a full URL there would double the domain.
 */
export const RESERVED_SOURCES = Object.freeze({ header_image: ['product_image_url'], button_url: ['product_slug'] });

/** Whether every slot, plus any reserved header_image/button_url mapping this template needs, is filled. */
export function mappingComplete(/** @type {WizardState} */ s, /** @type {any} */ template) {
  if (!template) return false;
  const allowed = variableChoicesFor(s.source).map((c) => c.value);
  const fits = (/** @type {'header_image' | 'button_url'} */ key) =>
    allowed.includes(s.variableMap[key]) && RESERVED_SOURCES[key].includes(s.variableMap[key]);
  const slotsOk = slotsFor(template).every((slot) => allowed.includes(s.variableMap[slot]));
  const headerOk = !needsHeaderImage(template) || fits('header_image');
  const buttonOk = !needsPerMessageLink(template) || fits('button_url');
  return slotsOk && headerOk && buttonOk;
}

/** variable_map for the request: the template's numbered slots, plus header_image/button_url when mapped and needed. */
export const requestVariableMap = (/** @type {WizardState} */ s, /** @type {any} */ template) => {
  const base = Object.fromEntries(slotsFor(template).map((slot) => [slot, s.variableMap[slot]]));
  if (needsHeaderImage(template) && s.variableMap.header_image) base.header_image = s.variableMap.header_image;
  if (needsPerMessageLink(template) && s.variableMap.button_url) base.button_url = s.variableMap.button_url;
  return base;
};

/** Everything that makes the created campaign what it is; a change to any of it makes a draft stale. */
export function campaignFingerprint(/** @type {WizardState} */ s, /** @type {any} */ template) {
  return JSON.stringify([
    previewBody(s),
    s.templateId,
    requestVariableMap(s, template),
    s.name.trim(),
    s.senderName.trim(),
    s.senderPosition.trim(),
  ]);
}

/** Why the sender fields block sending ('' when they do not): WhatsApp rejects an empty value the message uses. */
export function senderProblem(/** @type {WizardState} */ s, /** @type {any} */ template) {
  const used = Object.values(requestVariableMap(s, template));
  if (used.includes('sender_name') && !s.senderName.trim()) return 'Fill in Sender name: the message uses it';
  if (used.includes('sender_position') && !s.senderPosition.trim())
    return "Fill in Sender's position: the message uses it";
  return '';
}

/** Body of createCampaign, or null while anything is missing. */
export function campaignBody(/** @type {WizardState} */ s, /** @type {any} */ template) {
  const audience = previewBody(s);
  if (!audience || !template || !s.name.trim() || !mappingComplete(s, template) || senderProblem(s, template))
    return null;
  return {
    name: s.name.trim(),
    template_id: template.id,
    variable_map: requestVariableMap(s, template),
    sender_name: s.senderName.trim() || null,
    sender_position: s.senderPosition.trim() || null,
    ...audience,
  };
}

/** The value a preview shows for one placeholder source and person (sample values for event, style, size). */
function sampleValue(/** @type {string | undefined} */ key, /** @type {{name?: string | null} | undefined} */ person) {
  const name = String(person?.name ?? '')
    .replace(/\s+/g, ' ')
    .trim();
  if (key === 'first_name') return name.split(' ')[0] || 'there';
  if (key === 'full_name') return name || 'there';
  if (key && key in SAMPLE_VALUES) return SAMPLE_VALUES[/** @type {'style'} */ (key)];
  if (key === 'custom:company_name') return 'Your company';
  return key ? 'Sample' : '';
}

/** Ordered params for TemplatePreview: one value per slot, empty for a slot not mapped yet. */
export const previewParams = (/** @type {WizardState} */ s, /** @type {any} */ template, /** @type {any} */ person) =>
  slotsFor(template).map((slot) => sampleValue(s.variableMap[slot], person));

/** Default campaign name for the last step: "<template name> - 28 Sep 2026". */
export function defaultCampaignName(/** @type {string} */ templateName, now = new Date()) {
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${templateName} - ${now.getDate()} ${months[now.getMonth()]} ${now.getFullYear()}`;
}

const toggle = (/** @type {string[]} */ list, /** @type {string} */ id) =>
  list.includes(id) ? list.filter((x) => x !== id) : [...list, id];

/** @typedef {{type: 'patch', patch: Partial<WizardState>} | {type: 'toggleEvent' | 'toggleExcluded', id: string} | {type: 'source', source: string} | {type: 'template', id: string} | {type: 'map', slot: string, value: string} | {type: 'go', step: number, defaultName?: string}} Action */

function step(/** @type {WizardState} */ state, /** @type {Action} */ action) {
  switch (action.type) {
    case 'patch':
      return { ...state, ...action.patch };
    case 'toggleEvent':
      return { ...state, eventIds: toggle(state.eventIds, action.id) };
    case 'toggleExcluded':
      return { ...state, excluded: toggle(state.excluded, action.id) };
    case 'source': // another source offers other placeholders, so the mapping starts over
      return action.source === state.source
        ? state
        : { ...state, source: action.source, variableMap: {}, previewAs: 0 };
    case 'template':
      return action.id === state.templateId
        ? state
        : { ...state, templateId: action.id, variableMap: {}, previewAs: 0 };
    case 'map':
      return { ...state, variableMap: { ...state.variableMap, [action.slot]: action.value } };
    case 'go': {
      const name =
        action.step === LAST_STEP && !state.name.trim() && action.defaultName ? action.defaultName : state.name;
      return { ...state, step: action.step, name };
    }
    default:
      return state;
  }
}

/** Reducer. Ticks are per audience, so changing who the audience is clears the exclusions. */
export function wizardReducer(/** @type {WizardState} */ state, /** @type {Action} */ action) {
  const next = step(state, action);
  return next !== state && audienceKey(next) !== audienceKey(state) ? { ...next, excluded: [] } : next;
}
